import React, { useEffect, useState } from 'react';
import { chooserCardForCatalogId, type SetupDraft } from '@/utils/setupDraft';
import styled, { keyframes } from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Input from '../UI/Input';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   registerDevice,
   getCredits,
   fetchRouterScript,
   listDevices,
   pushLoginHtml,
   updateDeviceSetupStatusApi,
   verifyRouterConfig,
   applyRouterConfig,
   getWgStatus,
   updateDevice,
   listDhcpLeases,
   listIpBindings,
   addIpBinding,
   removeIpBinding,
   type RegisterDeviceResponse,
   type ApiVendoDevice,
   type SetupStatus,
   type VerifyRouterConfigResponse,
   type WgStatusResponse,
   type DhcpLease,
   type IpBindingEntry,
} from '@/api/vendo';
import NodeMcuFlasher from '../Vendo/NodeMcuFlasher';
import PinMapEditor from '../Vendo/PinMapEditor';
import RuijieRegister from './RuijieRegister';
import MikrotikWiring from './MikrotikWiring';
import { computeRegisterFinalStatus, canLeaveFlashStep } from './registerFinalStatus';
import { useDeviceCatalog } from '@/hooks/useDeviceCatalog';
import { CATALOG_ID_BY_CHOOSER_CARD } from '@/utils/deviceCatalogBoard';


type RouterModel = 'hex-rb750gr3' | 'rb4011' | 'ccr2004' | 'ccr2116';

type ApModel =
   | ''
   | 'tplink-eap110'
   | 'tplink-eap225'
   | 'comfast-cf-ew72'
   | 'comfast-cf-ew71'
   | 'comfast-cf-e380ac'
   | 'other';

type RegisterForm = {
   name: string;
   ssid: string;
   // How the NodeMCU reaches the network. 'wifi' joins the SSID above
   // (no LAN module needed); 'ethernet' is the classic wired W5500 path.
   connMode: 'wifi' | 'ethernet';
   wifiPassword: string;
   routerModel: RouterModel;
   apModel: ApModel;
   // ISP-uplink port on the MikroTik. hEX always uses 'ether1' (the UI
   // hides the picker). Larger routers (RB4011, CCRs) expose a dropdown
   // because operators may uplink on ether12, sfp+1, etc.
   wanPort: string;
   // True when the operator picks "Custom..." from the wanPort dropdown
   // — switches the UI to a free-text input so they can type sfp+1 etc.
   wanPortCustom: boolean;
};

// Per-router WAN port candidates. ether2..etherN come from routerModels,
// and we prepend ether1 (the canonical WAN) so it stays the visible default.
// SFP+ cages are listed too (ISP on fibre, e.g. Carl's CCR2116); other names still fit under "Custom...".
const WAN_PORT_OPTIONS: Record<RouterModel, readonly string[]> = {
   'hex-rb750gr3': ['ether1'], // dropdown hidden for hEX; included for completeness
   'rb4011': ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'sfp-sfpplus1'],
   'ccr2004': ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'ether14', 'ether15', 'ether16', 'sfp-sfpplus1', 'sfp-sfpplus2'],
   'ccr2116': ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'sfp-sfpplus1', 'sfp-sfpplus2', 'sfp-sfpplus3', 'sfp-sfpplus4'],
};

// Fixed-uplink models always WAN on ether1 — the wizard hides the WAN-port
// picker for these and forces ether1 on model switch.
const FIXED_WAN_ETHER1: ReadonlySet<RouterModel> = new Set(['hex-rb750gr3']);

// LAN ports per model (ether2..etherN). Mirrors the server's routerModels
// lanPorts — feeds the Step-2 port-role banner so operators can see where to
// plug customers vs an AP/switch vs their management laptop.
// The router's name on its wiring picture (MikrotikWiring).
const WIRING_TITLE: Record<RouterModel, string> = {
   'hex-rb750gr3': 'MikroTik hEX RB750Gr3',
   'rb4011': 'MikroTik RB4011',
   'ccr2004': 'MikroTik CCR2004',
   'ccr2116': 'MikroTik CCR2116',
};

const LAN_PORTS_BY_MODEL: Record<RouterModel, readonly string[]> = {
   'hex-rb750gr3': ['ether2', 'ether3', 'ether4', 'ether5'],
   'rb4011': ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'sfp-sfpplus1'],
   'ccr2004': ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'ether14', 'ether15', 'ether16', 'sfp-sfpplus1', 'sfp-sfpplus2'],
   'ccr2116': ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'sfp-sfpplus1', 'sfp-sfpplus2', 'sfp-sfpplus3', 'sfp-sfpplus4'],
};

type PortRole = 'wan' | 'customer' | 'trunk' | 'owner';

// Default port layout for the Step-2 banner. Mirrors the server registration
// default + resolvePortRoles: VLAN isolation is ON by default ONLY on the hEX
// (the model whose anti-config-copy lockdown is proven). Isolated hEX layout =
// ether2/3 customer (access), ether4 trunk (AP/switch/sub-vendos), ether5
// owner/management. Every other model stays FLAT → all LAN ports serve customers.
function portLayoutForModel(
   model: RouterModel,
   wanPort: string
): { ports: { port: string; role: PortRole }[]; isolated: boolean } {
   const wan = wanPort || 'ether1';
   const lan = (LAN_PORTS_BY_MODEL[model] ?? []).filter((p) => p !== wan);
   const isolated = model === 'hex-rb750gr3';
   const ports: { port: string; role: PortRole }[] = [{ port: wan, role: 'wan' }];
   if (!isolated) {
      // RB4011 / CCR run flat until the first sub-vendo switches VLANs on;
      // from then the server's resolvePortRoles applies: the first two LAN
      // ports stay Customer, the rest (SFP+ too) are Trunk. Show THAT layout:
      // a flat all-Customer picture left a CCR owner asking "which port is
      // the trunk?" (Ray 2026-10-21). No owner port by default here.
      lan.forEach((p, i) => ports.push({ port: p, role: i < 2 ? 'customer' : 'trunk' }));
      return { ports, isolated };
   }
   const owner = lan[lan.length - 1];
   lan
      .filter((p) => p !== owner)
      .forEach((p, i) => ports.push({ port: p, role: i < 2 ? 'customer' : 'trunk' }));
   if (owner) ports.push({ port: owner, role: 'owner' });
   return { ports, isolated };
}

const AP_MODEL_OPTIONS: ReadonlyArray<{ value: ApModel; label: string }> = [
   { value: 'tplink-eap110', label: 'TP-Link EAP110' },
   { value: 'tplink-eap225', label: 'TP-Link EAP225' },
   { value: 'comfast-cf-ew72', label: 'Comfast CF-EW72 (dual-band ceiling)' },
   { value: 'comfast-cf-ew71', label: 'Comfast CF-EW71 (2.4GHz ceiling)' },
   { value: 'comfast-cf-e380ac', label: 'Comfast CF-E380AC (outdoor)' },
   { value: 'other', label: 'Other / Not listed' },
];

// SSID validation: 1-32 octets, no control chars or null. RouterOS accepts
// most of UTF-8 but operators typically pick ASCII; we don't enforce that
// strictly so brand names with accents still work.
const SSID_MAX = 32;

const MAC_REGEX = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/;

// Router admin IP baked into every registration. The old Step-1 "Advanced
// settings" let the operator override this (and hand-type the NodeMCU MAC),
// but the wizard targets Starter-Kit defaults and the MAC is captured
// automatically by the flasher — both fields were removed 2026-06-12.
type LiteBoardKey =
   | 'ruijie'
   | 'orangepi'
   | 'orangepipc'
   | 'orangepizero3'
   | 'comfast'
   | 'comfast72'
   | 'rt2980'
   | 'rt2983'
   | 'newifid2'
   | 't6m'
   | 'minipc';

const DEFAULT_ROUTER_IP = '192.168.88.1';

const STEPS = [
   { num: 1, label: 'Name' },
   { num: 2, label: 'Hardware' },
   { num: 3, label: 'Flash NodeMCU' },
   { num: 4, label: 'Connect Router' },
   { num: 5, label: 'Done' },
] as const;

type StepNum = 1 | 2 | 3 | 4 | 5;

const hasWebSerial = (): boolean =>
   typeof navigator !== 'undefined' && 'serial' in navigator;

// Resume-mode jump table: an unfinished device opens the wizard at the
// step matching its setupStatus. Done devices land on Step 5 — used
// from the device list as a "view summary" path.
//
// 'connecting-portal' (= login.html pushed but not yet stamped done)
// also resumes at Step 4 because the push is bundled into Step 4's apply
// path; there is no separate push step in the wizard anymore.
const STATUS_TO_STEP: Record<SetupStatus, StepNum> = {
   flashing: 3,
   'configuring-router': 4,
   'connecting-portal': 4,
   done: 5,
};

interface Props {
   /**
    * When provided, the wizard opens in resume mode: skips Steps 1-2
    * (already committed) and jumps to the step matching the device's
    * current setupStatus. Pass an ApiVendoDevice from
    * useVendoDevicesStore — we need deviceSecret etc. for the manual
    * fallback panels.
    */
   resumeDevice?: ApiVendoDevice;
   /** A setup begun on smartfi.ph: the board is chosen and the names typed,
    *  so the picker is skipped and the fields arrive filled in. */
   setupDraft?: SetupDraft;
}

const DeviceRegister = ({ resumeDevice, setupDraft }: Props = {}) => {
   const { handleClose } = useUIStore();
   // The chooser draws whatever the operator named and pictured each hardware
   // type in Admin Panel -> WiFi settings -> Devices. Anything the catalog has
   // nothing for keeps the built-in name and glyph written into the card.
   const { labelFor: catalogLabel, iconFor: catalogIcon } = useDeviceCatalog();
   const tierName = (key: string, fallback: string) =>
      catalogLabel(CATALOG_ID_BY_CHOOSER_CARD[key], fallback);
   const tierIcon = (key: string, glyph: string) => {
      const url = catalogIcon(CATALOG_ID_BY_CHOOSER_CARD[key]);
      return url ? <TierIconImg src={url} alt="" /> : <>{glyph}</>;
   };
   const navigate = useNavigate();
   const queryClient = useQueryClient();
   const { data: creditsData } = useQuery({
      queryKey: ['credits'],
      queryFn: getCredits,
   });
   const credits = creditsData?.credits ?? 0;
   const hasCredits = credits > 0;

   const isResume = !!resumeDevice;
   // Router tier. Fresh registrations pick this first (chooser below); resume
   // reads it off the device so Lite devices reopen in the Lite wizard.
   // A NEW MikroTik registers as HYBRID (the router is the portal and keeps
   // selling with the internet down). Cloud is still SHOWN, marked Closed and
   // not selectable, so operators see the change (Ray, 2026-10-07: "you still
   // need to show the options [cloud (closed), Hybrid (Beta)]"). The live cloud
   // MikroTiks are not touched by this; they move only through the Switch to
   // Hybrid button. A resumed registration keeps the mode it was started with.
   const [mikrotikMode, setMikrotikMode] = useState<'cloud' | 'hybrid' | null>(
      resumeDevice ? (resumeDevice.router?.localMode ? 'hybrid' : 'cloud') : null
   );
   // A smartfi.ph draft names its board; an unknown one falls back to the picker.
   const draftCard = setupDraft ? chooserCardForCatalogId(setupDraft.board) : null;
   const [platform, setPlatform] = useState<'mikrotik' | 'openwrt' | null>(
      resumeDevice
         ? resumeDevice.router?.platform ?? 'mikrotik'
         : draftCard === 'mikrotik'
           ? 'mikrotik'
           : draftCard
             ? 'openwrt'
             : null
   );
   // Which OpenWRT board the operator picked (Ruijie or OrangePi). Set on the
   // single 3-way picker below and handed to RuijieRegister so it skips its own
   // board question. Null until an OpenWRT board is chosen.
   const [liteBoard, setLiteBoard] = useState<LiteBoardKey | null>(draftCard && draftCard !== 'mikrotik' ? (draftCard as LiteBoardKey) : null);
   // Lite (Ruijie/OpenWRT) is GA — available to all operators (was admin-only
   // while in pilot).
   const canUseLite = true;
   // OrangePi One is GA — available to all operators (was admin/staff-only
   // during the prod canary; canary passed 2026-07-18).
   const canUseOrangePi = true;
   // Resume bypasses the browser check — the operator already passed it the
   // first time.
   const [preflightDone, setPreflightDone] = useState(isResume);
   const [step, setStep] = useState<StepNum>(
      resumeDevice ? STATUS_TO_STEP[resumeDevice.setupStatus] : 1
   );
   // Default ON when the owner has credits — the trial flows straight
   // into a paid license at day-14 without action. Default OFF on a
   // zero-credit wallet so a fresh user isn't surprised by a charge later.
   const [autoRenew, setAutoRenew] = useState<boolean>(credits > 0);
   // Port layout and wiring picture as two tabs: both at once made step 2 taller than the screen (Ray 2026-10-21).
   const [portView, setPortView] = useState<'ports' | 'wiring'>('ports');
   const [form, setForm] = useState<RegisterForm>({
      name: setupDraft?.name || '',
      ssid: setupDraft?.ssid || 'SmartFiWiFi',
      connMode: 'wifi', // default wireless (LAN modules are out of stock)
      wifiPassword: '',
      routerModel: 'hex-rb750gr3',
      apModel: 'tplink-eap110',
      wanPort: 'ether1',
      wanPortCustom: false,
   });
   const [errors, setErrors] = useState<{
      name?: boolean;
      ssid?: boolean;
   }>({});
   const [isLoading, setIsLoading] = useState(false);
   // When resuming, synthesize a registered-shaped object from the
   // device record so Step 3+ panes can show credentials, slug, etc.
   // without an extra fetch.
   const [registered, setRegistered] = useState<RegisterDeviceResponse | null>(
      resumeDevice
         ? {
              msg: 'Resumed',
              device: {
                 _id: resumeDevice._id,
                 name: resumeDevice.name,
                 deviceId: resumeDevice.deviceId,
                 portalSlug: resumeDevice.portalSlug,
              },
              portalUrl: '',
              credentials: {
                 deviceId: resumeDevice.deviceId,
                 deviceSecret: resumeDevice.deviceSecret,
              },
           }
         : null
   );
   const [routerScript, setRouterScript] = useState<string | null>(null);
   const [bootstrapLine, setBootstrapLine] = useState<string | null>(null);
   // Second paste line for prod mode (the /import). Operator pastes this
   // AFTER the fetch in `bootstrapLine` completes. Chaining via ; was
   // unreliable in Winbox — see git log for context.
   const [bootstrapImport, setBootstrapImport] = useState<string | null>(null);
   const [scriptLoading, setScriptLoading] = useState(false);
   // Operator's setup-machine MAC. When provided, gets saved on the
   // device record before the script is fetched so the generator can
   // bake the ip-binding bypass into section 3 — operator keeps
   // internet after the import line lands.
   const [operatorMac, setOperatorMac] = useState('');
   const [operatorMacError, setOperatorMacError] = useState<string | null>(null);
   const MAC_REGEX = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/;
   // Apply-config: server walks each section via REST after the operator
   // pastes the bootstrap line. Replaces the old "paste full script in
   // terminal" flow that was eating sections via paste-truncation.
   // Resume hydration: if the device is already past Step 4, treat
   // applyState as 'applied' so computeFinalStatus() can recognize the
   // operator finished router config in a previous session.
   const [applyState, setApplyState] = useState<
      'idle' | 'applying' | 'applied' | 'failed'
   >(
      resumeDevice &&
         (resumeDevice.setupStatus === 'connecting-portal' ||
            resumeDevice.setupStatus === 'done')
         ? 'applied'
         : 'idle'
   );
   const [applyError, setApplyError] = useState<string | null>(null);
   // Step 4 paste-validation. After the operator pastes the script, they
   // click Verify config — server makes REST calls to the router to
   // confirm each section's effect. Catches paste-truncation in Winbox
   // before they advance to Step 5 and hit a confusing 401.
   const [verifyState, setVerifyState] = useState<
      'idle' | 'verifying' | 'verified' | 'failed'
   >('idle');
   const [verifyResult, setVerifyResult] =
      useState<VerifyRouterConfigResponse | null>(null);
   // Dev-mode script toggle — only rendered in dev builds. Lets admin testers
   // generate a LAN-pointed script (plain HTTP, no WireGuard, dev-server IP
   // in the firewall whitelist) without leaving the wizard for the standalone
   // Router Script admin modal.
   const [devMode, setDevMode] = useState(false);
   const [devServerIp, setDevServerIp] = useState('192.168.88.10');
   // Once Step 4 sees a fresh heartbeat we latch this true and stop
   // toasting / re-advancing on subsequent polls.
   const [routerDetected, setRouterDetected] = useState(false);
   // Step 4 substep accordion. Only one of 4a/4b open at a time so the
   // modal stays short on small laptops.
   const [step4Open, setStep4Open] = useState<'4a' | '4b' | null>('4a');
   useEffect(() => {
      if (bootstrapLine) setStep4Open('4b');
   }, [bootstrapLine]);
   // Whether NodeMCU flash actually completed (vs skipped). In-session
   // only — on Resume we trust setupStatus rather than tracking past
   // skips. If operator skipped on this run, finalize as 'flashing' so
   // the device list shows it as pending instead of done.
   const [nodeMcuFlashed, setNodeMcuFlashed] = useState(
      resumeDevice ? resumeDevice.setupStatus !== 'flashing' : false
   );
   // login.html push state. Set by Step 4's apply path (apply pushes
   // login.html as its last step) or the heartbeat-detection effect.
   // Resume hydration: if the device is already 'done', start as 'pushed'
   // so the Step 5 button reads "Finish setup" instead of "Save & continue
   // later".
   type PushState = 'idle' | 'pushing' | 'pushed' | 'error';
   const [pushState, setPushState] = useState<PushState>(
      resumeDevice && resumeDevice.setupStatus === 'done' ? 'pushed' : 'idle'
   );

   // Step 4 auto-detect: poll the device list while the user is on the
   // "Configure Router" step. A fresh heartbeat (status online + lastHeartbeat
   // within the last 60s) proves router config + WiFi + WireGuard + NodeMCU
   // are all working — the strongest possible signal that the script ran
   // successfully. When detected, advance to Step 5.
   const { data: polledDevice } = useQuery({
      queryKey: ['device-poll', registered?.device._id],
      queryFn: async () => {
         if (!registered) return null;
         const res = await listDevices();
         return res.devices.find((d) => d._id === registered.device._id) ?? null;
      },
      // Step 5 keeps polling too: it is where the operator learns whether the
      // NodeMCU ever came online (see coinBoxSilent below).
      enabled: (step === 4 || step === 5) && !!registered,
      refetchInterval: step === 4 || step === 5 ? 4000 : false,
      refetchIntervalInBackground: false,
   });

   // Step 4 prod-mode tunnel poll: separate from the device heartbeat poll
   // because the WG handshake lands a few seconds BEFORE the NodeMCU's
   // first heartbeat — we want to enable "Apply config" as soon as the
   // tunnel is reachable, then push the rest via REST. Returns provisioned:
   // false until the server has minted the peer (happens lazily on the
   // first fetchRouterScript call), then flips to isUp once the kernel
   // sees a fresh handshake.
   const { data: wgStatusRaw } = useQuery<WgStatusResponse>({
      queryKey: ['wg-status', registered?.device._id],
      queryFn: () => getWgStatus(registered!.device._id),
      enabled: step === 4 && !!registered && !devMode && !!bootstrapLine,
      refetchInterval:
         step === 4 && !devMode && !!bootstrapLine ? 3000 : false,
      refetchIntervalInBackground: false,
   });
   const wgStatus =
      wgStatusRaw && wgStatusRaw.provisioned ? wgStatusRaw : null;

   useEffect(() => {
      if (step !== 4 || routerDetected || !polledDevice) return;
      if (polledDevice.status !== 'online' || !polledDevice.lastHeartbeat) return;
      const fresh =
         Date.now() - new Date(polledDevice.lastHeartbeat).getTime() < 60_000;
      if (!fresh) return;

      setRouterDetected(true);
      // A fresh heartbeat = the script ran AND the router can reach the
      // VPS AND the NodeMCU is talking. Apply is done — promote
      // applyState to 'applied' so computeFinalStatus() on Step 5
      // returns 'done'. The router script does NOT auto-fetch login.html
      // (only handleApplyConfig's server-side path does that), so push it
      // explicitly here before advancing to the success step.
      setApplyState('applied');
      toast.success('Device online — router connected!', { autoClose: 2000 });
      void bumpSetupStatus('connecting-portal');
      void (async () => {
         try {
            setPushState('pushing');
            await pushLoginHtml(registered!.device._id);
            setPushState('pushed');
            void bumpSetupStatus('done');
         } catch (err) {
            // login.html push failed — the rest of setup is fine, so we
            // still advance to Step 5. computeFinalStatus() will resolve
            // to 'connecting-portal' and the button will read "Save &
            // continue later"; operator can re-push from the Devices
            // admin panel later.
            console.warn('[wizard] heartbeat-path pushLoginHtml failed:', err);
            setPushState('error');
            toast.warn(
               'Router connected, but login.html push failed — re-push from the Devices admin panel.'
            );
         } finally {
            setStep(5);
         }
      })();
   }, [step, polledDevice, routerDetected]);

   const update = <K extends keyof RegisterForm>(key: K, value: RegisterForm[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      if (errors[key as keyof typeof errors]) {
         setErrors((prev) => ({ ...prev, [key]: false }));
      }
   };

   const validateStep1 = (): boolean => {
      const newErrors: typeof errors = {};
      if (!form.name.trim()) newErrors.name = true;
      // SSID is only required for a WIRELESS NodeMCU (it's the AP the NodeMCU
      // joins). A wired NodeMCU doesn't use it, so we don't collect it.
      if (form.connMode === 'wifi') {
         const ssid = form.ssid.trim();
         if (!ssid) newErrors.ssid = true;
         else if (ssid.length > SSID_MAX) newErrors.ssid = true;
      }
      if (Object.keys(newErrors).length > 0) {
         setErrors(newErrors);
         toast.error(
            newErrors.ssid
               ? `WiFi name must be 1–${SSID_MAX} characters`
               : 'Please fill in required fields'
         );
         return false;
      }
      return true;
   };

   const handleStep1Next = () => {
      if (!validateStep1()) return;
      setStep(2);
   };

   const handleStep2Next = async () => {
      if (isLoading) return;
      setIsLoading(true);
      try {
         const wanPort = FIXED_WAN_ETHER1.has(form.routerModel)
            ? 'ether1'
            : form.wanPort.trim() || 'ether1';
         const res = await registerDevice({
            name: form.name.trim(),
            router: {
               model: form.routerModel,
               // Local-first is a property of the ROUTER config, so it travels
               // with it. Sent only when hybrid was actually chosen; a cloud
               // registration carries nothing new at all.
               ...(mikrotikMode === 'hybrid' ? { localMode: true } : {}),
               wanPort,
               ip: DEFAULT_ROUTER_IP,
               ssid: form.ssid.trim(),
            },
            ap: {
               model: form.apModel,
            },
            // The chip's MAC is never sent from here — the flasher captures
            // it over WebSerial in Step 3 (heartbeat is the fallback).
            ...(form.connMode === 'wifi'
               ? {
                    nodeMcu: {
                       connMode: form.connMode,
                       // The NodeMCU joins the SSID above; password is the
                       // operator's AP WiFi password (blank = open AP).
                       wifiSsid: form.ssid.trim(),
                       ...(form.wifiPassword ? { wifiPassword: form.wifiPassword } : {}),
                    },
                 }
               : {}),
            autoRenewLicense: autoRenew,
         });

         setRegistered(res);
         queryClient.invalidateQueries({ queryKey: ['credits'] });
         void useVendoDevicesStore.getState().fetch();
         toast.success(
            `Device "${res.device.name}" registered · 14-day trial begins on first heartbeat`,
            { autoClose: 2800 }
         );
         setStep(3);
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            (err as Error)?.message ||
            'Failed to register device';
         toast.error(msg);
      } finally {
         setIsLoading(false);
      }
   };

   // Quietly bump the wizard's setupStatus on the server. Best-effort —
   // a failure here doesn't block UI advance because the source of truth
   // is the user's local progress, not the server. Worst case the
   // device list shows them at the previous step until next transition.
   const bumpSetupStatus = async (next: SetupStatus) => {
      if (!registered) return;
      try {
         await updateDeviceSetupStatusApi(registered.device._id, next);
         // Refresh the global devices store so a subsequent Resume
         // click reads the new setupStatus, not the stale one. Without
         // this, closing the modal after a step-advance and clicking
         // Resume would reopen at the *previous* step (the value the
         // store cached before the bump).
         void useVendoDevicesStore.getState().fetch();
      } catch (err) {
         console.warn('[wizard] setupStatus PATCH failed (non-fatal):', err);
      }
   };

   // (login.html push lives inside Step 4's apply path and the heartbeat-
   // detection effect — there's no dedicated push step anymore. Manual
   // re-push for failure recovery is available from the Devices admin
   // panel column, not from the wizard.)

   const handleVerifyRouter = async () => {
      if (!registered) return;
      if (verifyState === 'verifying') return;
      setVerifyState('verifying');
      setVerifyResult(null);
      try {
         const res = await verifyRouterConfig(registered.device._id);
         setVerifyResult(res);
         if (res.ok) {
            setVerifyState('verified');
            // Verify says router config is present despite the earlier
            // apply error — treat as a real success: promote apply +
            // push login.html (router script doesn't auto-fetch it) and
            // advance to the final step.
            setApplyState('applied');
            void bumpSetupStatus('connecting-portal');
            setTimeout(async () => {
               try {
                  setPushState('pushing');
                  await pushLoginHtml(registered.device._id);
                  setPushState('pushed');
                  void bumpSetupStatus('done');
               } catch (pushErr) {
                  console.warn('[wizard] verify-path pushLoginHtml failed:', pushErr);
                  setPushState('error');
                  toast.warn(
                     'Router verified, but login.html push failed — re-push from the Devices admin panel.'
                  );
               } finally {
                  setStep(5);
               }
            }, 1200);
         } else {
            setVerifyState('failed');
         }
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            (err as Error)?.message ||
            'Verification failed';
         toast.error(msg);
         setVerifyState('failed');
      }
   };

   const loadRouterScript = async () => {
      if (!registered || routerScript || scriptLoading) return;
      if (devMode && !devServerIp.trim()) {
         toast.error('Dev mode requires a server IP');
         return;
      }
      // Validate + save operator MAC BEFORE fetching the script so the
      // generator can bake the bypass into section 3. Empty is allowed
      // (remote registration where operator isn't on the router's LAN);
      // non-empty must look like a MAC.
      const trimmedMac = operatorMac.trim();
      if (trimmedMac && !MAC_REGEX.test(trimmedMac)) {
         setOperatorMacError('Looks malformed — use AA:BB:CC:DD:EE:FF format');
         return;
      }
      setOperatorMacError(null);
      setScriptLoading(true);
      try {
         if (trimmedMac) {
            await updateDevice(registered.device._id, {
               operatorBypassMac: trimmedMac.toUpperCase(),
            });
         }
         const res = await fetchRouterScript(registered.device._id, {
            devMode,
            devServerIp: devServerIp.trim(),
         });
         setRouterScript(res.script);
         setBootstrapLine(res.bootstrap);
         setBootstrapImport(res.bootstrapImport);
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Could not fetch router script';
         toast.error(msg);
      } finally {
         setScriptLoading(false);
      }
   };

   const handleApplyConfig = async () => {
      if (!registered) return;
      if (applyState === 'applying') return;
      setApplyState('applying');
      setApplyError(null);
      try {
         const res = await applyRouterConfig(registered.device._id, {
            devMode,
            devServerIp: devServerIp.trim(),
            wait: true, // the wizard moves on only once the router is configured
         });
         if (res.ok) {
            setApplyState('applied');
            // Apply already pushed login.html + status.html as its last
            // step — mark pushState 'pushed' so computeFinalStatus() on
            // Step 5 resolves to 'done'.
            setPushState('pushed');
            void bumpSetupStatus('done');
            setTimeout(() => setStep(5), 1500);
         } else {
            setApplyState('failed');
            setApplyError(res.error || 'Apply failed');
         }
      } catch (err: unknown) {
         const errResp = (err as { response?: { data?: { error?: string } } })?.response?.data;
         const msg = errResp?.error || (err as Error)?.message || 'Apply failed';
         setApplyState('failed');
         setApplyError(msg);
      }
   };

   const copy = (text: string, label: string) => {
      navigator.clipboard
         .writeText(text)
         .then(() => toast.success(`${label} copied`, { autoClose: 1500 }))
         .catch(() => toast.error(`Could not copy ${label}`));
   };

   // Persist the operator's real completion state at the finish line. Rule and
   // rationale (including why a skipped NodeMCU flash no longer holds the
   // setup open) live in registerFinalStatus.ts, where they are unit-tested.
   const computeFinalStatus = (): SetupStatus =>
      computeRegisterFinalStatus({
         routerApplied: applyState === 'applied',
         portalPushed: pushState === 'pushed',
      });

   const goNext = () => {
      if (step === 1) return handleStep1Next();
      if (step === 2) return handleStep2Next();
      // Steps 3+ advance locally, but also bump setupStatus on the
      // server so Resume lines up with where the operator actually is.
      // Without this, closing the modal mid-wizard puts Resume one
      // step BEHIND the operator's actual progress.
      const nextStep = Math.min(5, step + 1) as StepNum;
      const STEP_TO_STATUS: Record<number, SetupStatus | undefined> = {
         3: 'flashing',
         4: 'configuring-router',
         5: computeFinalStatus(),
      };
      const nextStatus = STEP_TO_STATUS[nextStep];
      if (nextStatus) void bumpSetupStatus(nextStatus);
      setStep(nextStep);
   };

   /* ─── Step renderers ────────────────────────────────────── */

   const renderStep1 = () => (
      <>
         <LicenseSection>
            <SectionLabel>License</SectionLabel>
            <ChoiceCard type="button" $selected $static>
               <ChoiceIcon>⏱</ChoiceIcon>
               <ChoiceTitle>14-Day Trial</ChoiceTitle>
               <ChoiceDesc>
                  Starts automatically when your device first comes online.
               </ChoiceDesc>
               <ChoiceTag $green>Free</ChoiceTag>
            </ChoiceCard>
            <AutoRenewRow>
               <AutoRenewCheckbox
                  type="checkbox"
                  id="autoRenew"
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
               />
               <AutoRenewLabel htmlFor="autoRenew">
                  <AutoRenewTitle>Auto-activate — once you have credit</AutoRenewTitle>
                  <AutoRenewDesc>
                     {autoRenew && !hasCredits
                        ? 'Buy credits below — we will auto-charge 1 credit when the trial ends.'
                        : autoRenew
                        ? 'We will use 1 credit automatically when the trial ends — a lifetime license.'
                        : 'Trial ends after 14 days — portal will lock until you activate.'}
                  </AutoRenewDesc>
               </AutoRenewLabel>
            </AutoRenewRow>
            {autoRenew && !hasCredits && (
               <BuyCreditsCard
                  type="button"
                  onClick={() => {
                     handleClose();
                     navigate('/wifi-partners');
                  }}
               >
                  <BuyCreditsIcon>🛒</BuyCreditsIcon>
                  <BuyCreditsText>
                     <BuyCreditsTitle>0 credits in wallet — buy now</BuyCreditsTitle>
                     <BuyCreditsDesc>Browse partners selling credits</BuyCreditsDesc>
                  </BuyCreditsText>
                  <BuyCreditsArrow>→</BuyCreditsArrow>
               </BuyCreditsCard>
            )}
         </LicenseSection>

         <Input
            label="Device name *"
            name="name"
            value={form.name}
            placeholder="e.g. Vendo Unit 4"
            error={errors.name}
            onChange={(e) => update('name', e.target.value)}
         />
         <FieldLabel>NodeMCU connection</FieldLabel>
         <RadioRow>
            <RadioCard
               type="button"
               $selected={form.connMode === 'wifi'}
               onClick={() => update('connMode', 'wifi')}
            >
               <RadioTitle>Wireless</RadioTitle>
               <RadioDesc>Joins your WiFi — no LAN module</RadioDesc>
            </RadioCard>
            <RadioCard
               type="button"
               $selected={form.connMode === 'ethernet'}
               onClick={() => update('connMode', 'ethernet')}
            >
               <RadioTitle>Wired</RadioTitle>
               <RadioDesc>LAN module (W5500)</RadioDesc>
            </RadioCard>
         </RadioRow>

         {form.connMode === 'wifi' ? (
            <>
               <FieldRow>
                  <Input
                     label="WiFi name (SSID) *"
                     name="ssid"
                     value={form.ssid}
                     placeholder="your AP's exact name"
                     error={errors.ssid}
                     onChange={(e) => update('ssid', e.target.value)}
                  />
                  <Input
                     label="WiFi password"
                     name="wifiPassword"
                     value={form.wifiPassword}
                     placeholder="blank if open"
                     onChange={(e) => update('wifiPassword', e.target.value)}
                  />
               </FieldRow>
               <SsidHint>
                  The NodeMCU joins this WiFi — the name must match your access point
                  exactly (blank password = open WiFi). Baked into the chip in Step 3.
               </SsidHint>
            </>
         ) : (
            <SsidHint>
               The NodeMCU connects by Ethernet cable — no WiFi name needed.
            </SsidHint>
         )}

      </>
   );

   const renderStep2 = () => {
      const showWanPicker = !FIXED_WAN_ETHER1.has(form.routerModel);
      const portOptions = WAN_PORT_OPTIONS[form.routerModel];
      return (
         <HardwareSection>
            <FieldLabel>Router model</FieldLabel>
            <Select
               value={form.routerModel}
               onChange={(e) => {
                  const next = e.target.value as RouterModel;
                  const fixedWan = FIXED_WAN_ETHER1.has(next);
                  setForm((prev) => ({
                     ...prev,
                     routerModel: next,
                     // hEX always WAN=ether1, no picker — reset so switching
                     // from a CCR pick doesn't keep ether12.
                     wanPort: fixedWan ? 'ether1' : prev.wanPort,
                     wanPortCustom: fixedWan ? false : prev.wanPortCustom,
                  }));
               }}
            >
               <option value="hex-rb750gr3">MikroTik hEX RB750Gr3 (up to 20 sub-vendos)</option>
               <option value="rb4011">MikroTik RB4011 (up to 100 sub-vendos)</option>
               <option value="ccr2004">MikroTik CCR2004 (up to 500 sub-vendos)</option>
               <option value="ccr2116">MikroTik CCR2116 (up to 500 sub-vendos)</option>
            </Select>

            {(() => {
               const layout = portLayoutForModel(form.routerModel, form.wanPort);
               return (
                  <PortBanner>
                     <PortBannerTitle>
                        Port layout {layout.isolated ? '— isolated (VLAN 10)' : '— with sub-vendos'}
                     </PortBannerTitle>
                     <PortTabs role="tablist">
                        <PortTab type="button" role="tab" aria-selected={portView === 'ports'} $on={portView === 'ports'} onClick={() => setPortView('ports')}>Ports</PortTab>
                        <PortTab type="button" role="tab" aria-selected={portView === 'wiring'} $on={portView === 'wiring'} onClick={() => setPortView('wiring')}>Wiring picture</PortTab>
                     </PortTabs>
                     {portView === 'ports' && (
                     <PortRowWrap>
                        {layout.ports.map(({ port, role }) => (
                           <PortChip key={port} $color={ROLE_META[role].color}>
                              <PortChipPort>{port}</PortChipPort>
                              <PortChipRole $color={ROLE_META[role].color}>
                                 {ROLE_META[role].label}
                              </PortChipRole>
                           </PortChip>
                        ))}
                     </PortRowWrap>
                     )}
                     <PortLegend>
                        {layout.isolated
                           ? 'Plug customers into the green (Customer) ports. Trunk = a managed AP / switch / sub-vendos (a plain laptop here gets NO internet). Owner = your management laptop to reach the router — keep it for yourself.'
                           : `Until your first sub-vendo, every LAN port serves customers. Once you add one, ${layout.ports.filter((p) => p.role === 'customer').map((p) => p.port).join(' and ')} stay Customer and every other LAN port becomes Trunk: plug your managed switch or VLAN access point for sub-vendos there. You can change any port later in Networks → Ports.`}
                     </PortLegend>
                     {portView === 'wiring' && (
                        <MikrotikWiring
                           ports={layout.ports}
                           isolated
                           title={WIRING_TITLE[form.routerModel] ?? 'MikroTik'}
                        />
                     )}
                  </PortBanner>
               );
            })()}

            {showWanPicker && (
               <>
                  <FieldLabel>WAN port (your internet uplink)</FieldLabel>
                  {form.wanPortCustom ? (
                     <Input
                        name="wanPort"
                        value={form.wanPort}
                        placeholder="e.g. sfp+1, sfp-sfpplus1"
                        onChange={(e) => update('wanPort', e.target.value.trim())}
                     />
                  ) : (
                     <Select
                        value={form.wanPort}
                        onChange={(e) => {
                           const v = e.target.value;
                           if (v === '__custom__') {
                              setForm((prev) => ({ ...prev, wanPortCustom: true, wanPort: '' }));
                           } else {
                              update('wanPort', v);
                           }
                        }}
                     >
                        {/* ether1 first, then the fibre choices (SFP+ ports + Custom) at the
                            top where a fibre uplink owner looks first, then the copper
                            ports (Ray 2026-10-21). */}
                        {[
                           ...portOptions.filter((p) => p === 'ether1'),
                           ...portOptions.filter((p) => p.startsWith('sfp')),
                        ].map((p) => (
                           <option key={p} value={p}>{p}{p === 'ether1' ? ' (default WAN)' : ' (fibre)'}</option>
                        ))}
                        <option value="__custom__">Custom… (SFP / fibre)</option>
                        {portOptions
                           .filter((p) => p !== 'ether1' && !p.startsWith('sfp'))
                           .map((p) => (
                              <option key={p} value={p}>{p}</option>
                           ))}
                     </Select>
                  )}
                  {form.wanPortCustom && (
                     <AdvancedNote>
                        Type your uplink port name as RouterOS shows it in <code>/interface ethernet print</code> or <code>/interface print</code>.{' '}
                        <a
                           href="#"
                           onClick={(e) => {
                              e.preventDefault();
                              setForm((prev) => ({ ...prev, wanPortCustom: false, wanPort: 'ether1' }));
                           }}
                        >
                           Back to dropdown
                        </a>
                     </AdvancedNote>
                  )}
               </>
            )}

            <FieldLabel>AP model</FieldLabel>
            <Select
               value={form.apModel}
               onChange={(e) => update('apModel', e.target.value as ApModel)}
            >
               {AP_MODEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
               ))}
            </Select>
         </HardwareSection>
      );
   };

   const renderStep3 = () => (
      <>
         {registered ? (
            <>
            <PinMapEditor
               deviceId={registered.device._id}
               wired={form.connMode === 'ethernet'}
            />
            <NodeMcuFlasher
               offerFlashLater
               deviceId={registered.device._id}
               onSuccess={() => {
                  setNodeMcuFlashed(true);
                  void bumpSetupStatus('configuring-router');
                  setStep(4);
               }}
               onSkip={() => {
                  setNodeMcuFlashed(false);
                  void bumpSetupStatus('configuring-router');
                  setStep(4);
               }}
            />
            </>
         ) : (
            <ManualBox>
               <ManualHeader>Device not registered yet</ManualHeader>
               <Ol>
                  <li>Go back to Step 1 and register first.</li>
               </Ol>
            </ManualBox>
         )}

      </>
   );

   const renderStep4 = () => (
      <Step4Split $split={!!bootstrapLine}>
         <Step4Main>
         <StepCard $active={step4Open === '4a'}>
            <ToggleHeader
               type="button"
               onClick={() => setStep4Open(step4Open === '4a' ? null : '4a')}
               aria-expanded={step4Open === '4a'}
            >
               <StepBadge $active={step4Open === '4a'}>1</StepBadge>
               <StepTitleBlock>
                  <StepEyebrow $active={step4Open === '4a'}>Step 4a · Prepare</StepEyebrow>
                  <StepTitle>Prepare your router (one-time setup)</StepTitle>
                  {step4Open !== '4a' && <TapHint>👆 Tap to open</TapHint>}
               </StepTitleBlock>
               <Chevron $open={step4Open === '4a'}>▾</Chevron>
            </ToggleHeader>
            {step4Open === '4a' && (
               <>
                  <ScriptRow>
                     <ScriptLabel>Paste once in Winbox Terminal, then power-cycle the router:</ScriptLabel>
                     <CopyBtn onClick={() => copy(DEVICE_MODE_PREP_CMD, 'Prepare command')}>
                        Copy
                     </CopyBtn>
                  </ScriptRow>
                  <ScriptPre>{DEVICE_MODE_PREP_CMD}</ScriptPre>
                  <Ol $start={1}>
                     <li>Within 60s: <strong>unplug the router, wait 3s, plug back in.</strong></li>
                  </Ol>
                  <WhyDetails>
                     <summary>Why is this needed?</summary>
                     <p>
                        New MikroTik routers (RouterOS 7.13+) ship in a restricted{' '}
                        <Mono>home</Mono> mode that blocks the hotspot, fetch tool, and
                        scheduler. This switches to <Mono>advanced</Mono> mode; the
                        power-cycle is MikroTik&apos;s owner-confirmation.
                     </p>
                  </WhyDetails>
               </>
            )}
         </StepCard>

         <StepCard $active={step4Open === '4b'}>
            <ToggleHeader
               type="button"
               onClick={() => setStep4Open(step4Open === '4b' ? null : '4b')}
               aria-expanded={step4Open === '4b'}
            >
               <StepBadge $active={step4Open === '4b'}>2</StepBadge>
               <StepTitleBlock>
                  <StepEyebrow $active={step4Open === '4b'}>Step 4b · Connect</StepEyebrow>
                  <StepTitle>Connect your router to SmartFi</StepTitle>
                  {step4Open !== '4b' && <TapHint>👆 Tap to open</TapHint>}
               </StepTitleBlock>
               <Chevron $open={step4Open === '4b'}>▾</Chevron>
            </ToggleHeader>
            {step4Open === '4b' && (
               <>
            <ScriptLabel>
               Get the bootstrap line, paste it into Winbox Terminal
               {!devMode && ' (it also brings up the WireGuard tunnel)'}, then Apply.
            </ScriptLabel>
            <WhyDetails>
               <summary>How do I paste it?</summary>
               <p>
                  Open Winbox (or WebFig) → connect to your hEX → open{' '}
                  <Mono>Terminal</Mono> from the side menu → paste the line. One line
                  only — paste-truncation safe.
               </p>
            </WhyDetails>

            {!routerScript && (
               <WhyDetails>
                  <summary>Keep my setup laptop online (optional)</summary>
                  <OperatorMacBox>
                     <OperatorMacHint>
                        Bypasses your setup machine so it keeps internet after the
                        hotspot starts intercepting. Find it under WiFi / Ethernet
                        settings → &quot;MAC address&quot;. Leave blank for remote
                        registrations.
                     </OperatorMacHint>
                     <OperatorMacInput
                        id="wizard-operator-mac"
                        type="text"
                        placeholder="AA:BB:CC:DD:EE:FF"
                        value={operatorMac}
                        onChange={(e) => {
                           setOperatorMac(e.target.value);
                           if (operatorMacError) setOperatorMacError(null);
                        }}
                        spellCheck={false}
                        autoCapitalize="characters"
                     />
                     {operatorMacError && (
                        <OperatorMacErr>{operatorMacError}</OperatorMacErr>
                     )}
                  </OperatorMacBox>
               </WhyDetails>
            )}

            {import.meta.env.DEV && !routerScript && (
               <DevModeBox>
                  <DevModeRow>
                     <DevModeCheck
                        type="checkbox"
                        id="wizard-router-dev"
                        checked={devMode}
                        onChange={(e) => setDevMode(e.target.checked)}
                     />
                     <label htmlFor="wizard-router-dev">
                        <strong>Dev mode</strong> — plain HTTP, no WireGuard, LAN-scoped firewall
                     </label>
                  </DevModeRow>
                  {devMode && (
                     <DevModeIpRow>
                        <DevModeIpLabel>Dev server IP (your Mac on the hEX LAN)</DevModeIpLabel>
                        <DevModeIpInput
                           type="text"
                           placeholder="192.168.88.10"
                           value={devServerIp}
                           onChange={(e) => setDevServerIp(e.target.value)}
                        />
                     </DevModeIpRow>
                  )}
               </DevModeBox>
            )}

            <ScriptRow>
               <Btn
                  title={
                     scriptLoading
                        ? 'Loading...'
                        : routerScript
                        ? 'Bootstrap ready below'
                        : 'Get bootstrap line'
                  }
                  type={scriptLoading || routerScript ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={scriptLoading || routerScript ? colors.GRAY10 : colors.PRIMARY}
                  textcolor={scriptLoading || routerScript ? colors.GRAY1 : colors.WHITE}
                  onClick={loadRouterScript}
               />
               {bootstrapLine && (
                  <CopyBtn onClick={() => copy(bootstrapLine, 'Step 1 (fetch)')}>
                     {bootstrapImport ? 'Copy step 1 (fetch)' : 'Copy bootstrap'}
                  </CopyBtn>
               )}
            </ScriptRow>

            {bootstrapLine && <ScriptPre>{bootstrapLine}</ScriptPre>}

            {/* Prod mode only: second paste line. Two-paste split because
                Winbox terminal swallowed the chained /import after the
                /tool fetch progress output. Operator pastes step 1, waits
                for the prompt to return, then pastes step 2. */}
            {bootstrapImport && (
               <>
                  <ScriptRow style={{ marginTop: 12 }}>
                     <ScriptLabel>
                        Step 2 — paste this AFTER the fetch shows
                        "downloaded" and the prompt returns:
                     </ScriptLabel>
                     <CopyBtn onClick={() => copy(bootstrapImport, 'Step 2 (import)')}>
                        Copy step 2 (import)
                     </CopyBtn>
                  </ScriptRow>
                  <ScriptPre>{bootstrapImport}</ScriptPre>
               </>
            )}
               </>
            )}
         </StepCard>
         </Step4Main>

         {/* Once the bootstrap is fetched, the tunnel-handshake + Apply panels
             (and, on failure, the error + diagnostic) move to a 2nd column so
             the step stops growing tall. Stacks below on narrow/phone screens. */}
         {bootstrapLine && (
            <Step4Side>
               {/* WG handshake status while the operator pastes. */}
               {!devMode && (
                  <WgPanel $up={!!wgStatus?.isUp}>
                     <strong>
                        {wgStatus?.isUp
                           ? `Tunnel up — handshake at ${wgStatus.latestHandshakeAt ? new Date(wgStatus.latestHandshakeAt).toLocaleTimeString() : '—'}`
                           : 'Waiting for tunnel handshake…'}
                     </strong>
                     <WgPanelHint>
                        {wgStatus?.isUp
                           ? `Router reachable at ${wgStatus.vpnIp} over WireGuard.`
                           : 'After pasting the bootstrap line into Winbox, the hEX dials this server\'s tunnel. Usually completes within 5–15 s.'}
                     </WgPanelHint>
                  </WgPanel>
               )}

               <VerifyPanel $state={
                  applyState === 'idle' ? 'idle'
                  : applyState === 'applying' ? 'verifying'
                  : applyState === 'applied' ? 'verified'
                  : 'failed'
               }>
                  <VerifyHeader>
                     <strong>
                        {applyState === 'idle' && 'After pasting the bootstrap line, click Apply'}
                        {applyState === 'applying' && 'Applying configuration to the router…'}
                        {applyState === 'applied' && 'Configuration applied — advancing'}
                        {applyState === 'failed' && 'Apply failed'}
                     </strong>
                     {applyState === 'idle' && (
                        <VerifyHint>
                           Server walks every section (bridge, hotspot, walled-garden, hooks…)
                           via the router's REST API. Idempotent — safe to re-run.
                        </VerifyHint>
                     )}
                     {applyState === 'applying' && (
                        <VerifyHint>
                           ~5-10 seconds. The hotspot will reconfigure mid-apply, so your
                           Winbox session may briefly disconnect — that's normal.
                        </VerifyHint>
                     )}
                  </VerifyHeader>

                  {applyState !== 'applied' && (() => {
                     const tunnelGate = !devMode && !wgStatus?.isUp;
                     const disabled = applyState === 'applying' || tunnelGate;
                     return (
                        <Btn
                           title={
                              applyState === 'applying'
                                 ? 'Applying…'
                                 : tunnelGate
                                 ? 'Waiting for tunnel…'
                                 : 'Apply config'
                           }
                           type={disabled ? 'disabled' : 'normal'}
                           size="small"
                           bgcolor={disabled ? colors.GRAY10 : colors.PRIMARY}
                           textcolor={disabled ? colors.GRAY1 : colors.WHITE}
                           onClick={disabled ? () => {} : handleApplyConfig}
                        />
                     );
                  })()}
               </VerifyPanel>

               {/* On a failed apply, also show the error + per-section
                   diagnostic in this column. */}
               {applyState === 'failed' && (
                  <>
               <VerifyPanel $state="failed">
                  <VerifyHeader>
                     <strong>Error details</strong>
                     <VerifyHint>
                        The router rejected or didn&apos;t finish a section. Apply is
                        idempotent — fix the cause and click Apply again.
                     </VerifyHint>
                  </VerifyHeader>
                  {applyError && <VerifyErr>{applyError}</VerifyErr>}
               </VerifyPanel>

               <VerifyPanel $state={verifyState}>
                  <VerifyHeader>
                     <strong>
                        {verifyState === 'idle' && 'Diagnostic — verify per-section state'}
                        {verifyState === 'verifying' && 'Verifying…'}
                        {verifyState === 'verified' && 'All sections present (despite apply error)'}
                        {verifyState === 'failed' && 'Per-section status'}
                     </strong>
                  </VerifyHeader>

                  {verifyResult && verifyResult.checks && (
                     <VerifyChecklist>
                        <VerifyCheckRow $ok={verifyResult.checks.smartfiApiAuth}>
                           {verifyResult.checks.smartfiApiAuth ? '✓' : '✗'} smartfi-api auth
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.smartfiBridge}>
                           {verifyResult.checks.smartfiBridge ? '✓' : '✗'} smartfi-bridge
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.lanList}>
                           {verifyResult.checks.lanList ? '✓' : '✗'} LAN interface list
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.hotspot}>
                           {verifyResult.checks.hotspot ? '✓' : '✗'} hotspot exists
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.hotspotEnabled}>
                           {verifyResult.checks.hotspotEnabled ? '✓' : '✗'} hotspot enabled
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.walledGarden}>
                           {verifyResult.checks.walledGarden ? '✓' : '✗'} walled-garden ≥5 entries
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.smartfiUsersProfile}>
                           {verifyResult.checks.smartfiUsersProfile ? '✓' : '✗'} smartfi-users hooks
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.dnsRemoteRequests}>
                           {verifyResult.checks.dnsRemoteRequests ? '✓' : '✗'} router answers DNS
                           (customers can browse)
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.ipv6Disabled}>
                           {verifyResult.checks.ipv6Disabled ? '✓' : '✗'} IPv6 disabled
                        </VerifyCheckRow>
                        <VerifyCheckRow $ok={verifyResult.checks.operatorLockout}>
                           {verifyResult.checks.operatorLockout ? '✓' : '✗'} router locked to SmartFi
                        </VerifyCheckRow>
                     </VerifyChecklist>
                  )}
                  {verifyResult?.error && <VerifyErr>{verifyResult.error}</VerifyErr>}

                  <Btn
                     title={verifyState === 'verifying' ? 'Verifying…' : 'Verify per-section'}
                     type={verifyState === 'verifying' ? 'disabled' : 'normal'}
                     size="small"
                     bgcolor={verifyState === 'verifying' ? colors.GRAY10 : colors.GRAY10}
                     textcolor={verifyState === 'verifying' ? colors.GRAY1 : colors.MAIN_DARK}
                     onClick={handleVerifyRouter}
                  />
               </VerifyPanel>
                  </>
               )}
            </Step4Side>
         )}
      </Step4Split>
   );

   // The NodeMCU has had a few minutes and has never been heard from. Bench,
   // 2026-10-01: a mistyped WiFi name (SMARFI_DEV for SMARTFI_DEV) left a
   // flashed NodeMCU unable to join anything, and nothing on any screen said
   // so. Name the network it is looking for, so the operator can compare it
   // with the one their access point broadcasts.
   const [step5At, setStep5At] = useState(() => Date.now());
   useEffect(() => {
      if (step === 5) setStep5At(Date.now());
   }, [step]);
   const [nowTick, setNowTick] = useState(Date.now());
   useEffect(() => {
      if (step !== 5) return;
      const t = setInterval(() => setNowTick(Date.now()), 15_000);
      return () => clearInterval(t);
   }, [step]);
   const coinBoxSilent =
      step === 5 &&
      nodeMcuFlashed &&
      nowTick - step5At > COINBOX_SILENT_AFTER_MS &&
      !polledDevice?.nodeMcu?.lastHeartbeat;
   const coinBoxWifi = polledDevice?.nodeMcu?.connMode === 'wifi'
      ? polledDevice?.nodeMcu?.wifiSsid || polledDevice?.router?.ssid || ''
      : '';

   const renderStep5 = () => (
      <>
         {coinBoxSilent && (
            <CoinBoxSilent role="alert" data-testid="coinbox-silent">
               <strong>The NodeMCU has not connected yet.</strong>{' '}
               {coinBoxWifi ? (
                  <>
                     It is looking for the WiFi named <Mono>{coinBoxWifi}</Mono>. Check that
                     your access point broadcasts exactly that name (capital letters count),
                     then unplug the NodeMCU and plug it back in.
                  </>
               ) : (
                  <>Check its network cable, then unplug the NodeMCU and plug it back in.</>
               )}
            </CoinBoxSilent>
         )}
         <SuccessBanner>
            <SuccessIcon>✓</SuccessIcon>
            <SuccessText>
               <SuccessTitle>Device registered</SuccessTitle>
               {registered && (
                  <SuccessSub>
                     <strong>{registered.device.name}</strong> is ready. Portal:{' '}
                     <Mono>{registered.portalUrl}</Mono>
                  </SuccessSub>
               )}
            </SuccessText>
         </SuccessBanner>

         <BypassPanel deviceId={registered?.device._id ?? null} />
      </>
   );

   /* ─── Nav buttons ───────────────────────────────────────── */

   const renderNav = () => {
      const isLastStep = step === 5;
      // Back rules:
      //  - pre-register (step 2 only): back to step 1 to edit name/license-choice
      //  - post-register (step 4 only): back to revisit hardware/flash
      //  - never below step 3 once registered — name/hardware are committed
      //  - never on step 5 (terminal success state)
      const showBack =
         (!registered && step === 2) || (registered && step === 4);
      const backTarget: StepNum =
         step === 2 ? 1 : step === 4 ? 3 : step;

      // Per-step gate: keep Next disabled until the current step's
      // success signal is in place. Step 5 (finish line) is never gated
      // — the button there commits whatever progress the operator made.
      const nextDisabled =
         step === 1
            ? !form.name.trim()
            : step === 3
            ? !canLeaveFlashStep(nodeMcuFlashed, false) // the flasher's own "later" tick shows its own Next
            : step === 4
            ? applyState !== 'applied'
            : false;
      const btnDisabled = isLoading || nextDisabled;

      return (
         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <Spacer />
            {showBack && (
               <div onClick={() => setStep(backTarget)}>
                  <Btn title="← Back" type="outline" size="small" />
               </div>
            )}
            <div
               onClick={
                  btnDisabled
                     ? undefined
                     : isLastStep
                     ? () => {
                        // Persist the operator's actual completion state.
                        // If they skipped flash / router / portal, this
                        // resolves to a non-'done' status so the device
                        // list renders it as Setup in progress instead of
                        // a finished one they never actually finished.
                        const finalStatus = computeFinalStatus();
                        void bumpSetupStatus(finalStatus);
                        handleClose();
                     }
                     : goNext
               }
            >
               <Btn
                  title={
                     isLoading
                        ? 'Working...'
                        : isLastStep
                        ? computeFinalStatus() === 'done'
                           ? 'Finish setup'
                           : 'Save & continue later'
                        : 'Next →'
                  }
                  type={btnDisabled ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={btnDisabled ? colors.GRAY10 : colors.PRIMARY}
                  textcolor={btnDisabled ? colors.GRAY1 : colors.WHITE}
               />
            </div>
         </BtnRow>
      );
   };

   /* ─── Pre-flight gate ───────────────────────────────────── */

   const webSerialOk = hasWebSerial();

   // Lite (Ruijie / OpenWRT) routers use a separate bind-only wizard.
   // Resume passes the device through so it reopens at the NodeMCU step
   // instead of asking for a claim code the operator no longer has.
   if (platform === 'openwrt') {
      return (
         <RuijieRegister
            resumeDevice={resumeDevice}
            initialBoard={liteBoard ?? undefined}
            setupDraft={setupDraft}
         />
      );
   }

   // MikroTik picked, but not yet cloud-or-local. Asked here rather than buried
   // in a settings screen because it is not a setting: it decides what gets
   // flashed onto the NodeMCU and what config the router receives, and it
   // cannot be changed afterwards without touching the hardware.
   // Hybrid registration is open to everyone again (Ray, 2026-10-12, after
   // the hEX fixes were proven on the bench). "Switch to Hybrid" on an
   // EXISTING Cloud vendo stays admin-only until Hybrid is proven in the
   // field — see the Updates tab. The flag stays so the next pause is one line.
   const hybridRegistrationOpen = true;
   if (platform === 'mikrotik' && mikrotikMode === null) {
      return (
         <Wrapper>
            <PreflightTitle>How should this vendo sell?</PreflightTitle>
            <ModeRow>
               {/* Hybrid is in beta: while it is, only an admin registers a new
                   MikroTik (Ray, 2026-10-08: "no cloud. still disable cloud,
                   disable hybrid. only enable on admin"). Everyone else sees
                   both choices so they know what is coming, and can pick neither. */}
               {/* Cloud is open again for new MikroTik vendos while Hybrid is
                   in beta observation (Ray, 2026-10-09). */}
               <TierCard type="button" onClick={() => setMikrotikMode('cloud')}>
                  <TierIcon>☁️</TierIcon>
                  <TierText>
                     <TierNameRow>
                        <TierName>Cloud</TierName>
                     </TierNameRow>
                     <TierModel>Coins handled online</TierModel>
                     <TierDesc>
                        Coins handled by SmartFi online; needs internet to take a
                        coin.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!hybridRegistrationOpen}
                  aria-disabled={!hybridRegistrationOpen ? 'true' : undefined}
                  title={hybridRegistrationOpen ? undefined : 'New MikroTik vendos are paused for now. Vendos already running are not affected.'}
                  onClick={() => hybridRegistrationOpen && setMikrotikMode('hybrid')}
               >
                  <TierIcon>⚡</TierIcon>
                  <TierText>
                     <TierNameRow>
                        <TierName>Hybrid</TierName>
                        <BetaBadge title="New. Still being tested on real vendos.">
                           {hybridRegistrationOpen ? 'Beta' : 'Paused for now'}
                        </BetaBadge>
                     </TierNameRow>
                     <TierModel>Keeps running offline</TierModel>
                     <TierDesc>
                        The router holds the sessions itself: when the internet
                        is down, customers keep their time, can pause and resume,
                        and voucher codes still work. New coin sales wait for the
                        internet. You still manage everything here.
                     </TierDesc>
                  </TierText>
               </TierCard>
            </ModeRow>
            <BackLink type="button" onClick={() => setPlatform(null)}>
               ← Back
            </BackLink>
         </Wrapper>
      );
   }

   // Fresh registration: pick the device before anything else — one flat row,
   // explicit device names (no "Standard"/"Lite" tiers).
   if (platform === null) {
      return (
         <ChooserWrapper $wide>
            <PreflightTitle>Choose your device</PreflightTitle>
            <ChooserGrid>
               {/* Row 1 = the boards new in this release, each with a New pill so
                   the operator spots them without scrolling (Ray 2026-10-20,
                   "option b"): 12 cards, 3 columns, no empty cell, no "Soon". */}
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('t6m');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('t6m', '🚀')}</TierIcon>
                  <TierText>
                     <TierName>
                        {tierName('t6m', '360 T6M')} <NewPill>New</NewPill>
                     </TierName>
                     <TierModel>WiFi 6 all-in-one (AX1800)</TierModel>
                     <TierDesc>
                        WiFi 6, 3 gigabit ports, 256MB RAM. Used units often come
                        with OpenWrt already on. Flash from its OpenWrt page, bind
                        by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('orangepizero3');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('orangepizero3', '🍊')}</TierIcon>
                  <TierText>
                     <TierName>
                        {tierName('orangepizero3', 'OrangePi Zero 3')} <NewPill>New</NewPill>
                     </TierName>
                     <TierModel>Single-board vendo</TierModel>
                     <TierDesc>
                        Successor to the One: gigabit port, 64-bit H618. On-board
                        coin reader — no NodeMCU. USB WiFi or a wired AP. Flash the
                        SD card, bind by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('orangepipc');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('orangepipc', '🍊')}</TierIcon>
                  <TierText>
                     <TierName>
                        {tierName('orangepipc', 'OrangePi PC')} <NewPill>New</NewPill>
                     </TierName>
                     <TierModel>Single-board vendo</TierModel>
                     <TierDesc>
                        The One&apos;s bigger twin (H3, 1GB), same header. On-board
                        coin reader — no NodeMCU. Flash the SD card, bind by claim
                        code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard type="button" onClick={() => setPlatform('mikrotik')}>
                  <TierIcon>{tierIcon('mikrotik', '🌐')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('mikrotik', 'MikroTik')}</TierName>
                     <TierModel>Router + access point</TierModel>
                     <TierDesc>
                        Full features: VLAN sub-vendos, wired AP. For bigger
                        sites. Flash NodeMCU + configure router.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('ruijie');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('ruijie', '🛜')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('ruijie', 'Ruijie')}</TierName>
                     <TierModel>All-in-one router</TierModel>
                     <TierDesc>
                        Cheap all-in-one with built-in WiFi. For small shops.
                        Flash one image, bind by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               {canUseOrangePi && (
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('orangepi');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('orangepi', '🍊')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('orangepi', 'OrangePi One')}</TierName>
                     <TierModel>Single-board vendo</TierModel>
                     <TierDesc>
                        This exact board only (Allwinner H3). On-board coin
                        reader — no NodeMCU. USB WiFi or a wired AP. Flash the SD
                        card, bind by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               )}
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('comfast');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('comfast', '🛜')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('comfast', 'Comfast CF-EW71 v2')}</TierName>
                     <TierModel>Small ceiling AP</TierModel>
                     <TierDesc>
                        2.4GHz-only AP for a far-throw or open-air spot. Flash one
                        image over its recovery page, flash a NodeMCU, bind by
                        claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('comfast72');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('comfast72', '📶')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('comfast72', 'Comfast CF-EW72 V2')}</TierName>
                     <TierModel>Dual-band ceiling AP</TierModel>
                     <TierDesc>
                        Dual-band, 2 gigabit ports — the stronger outdoor AP.
                        Flash over its recovery page, flash a NodeMCU, bind by
                        claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('minipc');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('minipc', '🖥️')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('minipc', 'Mini PC (x86)')}</TierName>
                     <TierModel>Headend for many sub-vendos</TierModel>
                     <TierDesc>
                        No WiFi of its own — access points plug in. Flash its
                        SSD with one image, flash a NodeMCU, bind by claim code.
                        Spare ports carry sub-vendo networks.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('rt2980');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('rt2980', '🚀')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('rt2980', 'SmartFi (Edup RT2980-AX3000)')}</TierName>
                     <TierModel>WiFi 6 all-in-one</TierModel>
                     <TierDesc>
                        Fast WiFi 6, 4 gigabit ports, 256MB RAM. The strongest
                        board — for busy shops. Flash from its OpenWrt page, bind
                        by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('rt2983');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('rt2983', '🚀')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('rt2983', 'SmartFi (Edup RT2983-AX1800)')}</TierName>
                     <TierModel>WiFi 6 all-in-one</TierModel>
                     <TierDesc>
                        WiFi 6, 4 gigabit ports, 256MB RAM. A solid main-shop
                        router. Flash from its OpenWrt page, bind by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
               <TierCard
                  type="button"
                  disabled={!canUseLite}
                  onClick={
                     canUseLite
                        ? () => {
                             setLiteBoard('newifid2');
                             setPlatform('openwrt');
                          }
                        : undefined
                  }
               >
                  <TierIcon>{tierIcon('newifid2', '📶')}</TierIcon>
                  <TierText>
                     <TierName>{tierName('newifid2', 'Newifi D2')}</TierName>
                     <TierModel>WiFi 5 router, 4 LAN</TierModel>
                     <TierDesc>
                        WiFi 5 (AC1200), 4 gigabit ports, 512MB RAM. A budget
                        main-shop router. First flash goes through its Breed
                        recovery page, then bind by claim code.
                     </TierDesc>
                  </TierText>
               </TierCard>
            </ChooserGrid>
            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
            </BtnRow>
         </ChooserWrapper>
      );
   }

   // Only shown when this browser cannot run the flasher. The hardware checklist
   // that used to sit here was four boxes to tick before every registration
   // (Ray, 2026-10-01: "no need this"); with a working browser the wizard now
   // starts straight away.
   if (!preflightDone && !webSerialOk) {
      return (
         <Wrapper>
            <PreflightTitle>Before we start</PreflightTitle>
            <PreflightSection>
               <PreflightSectionLabel>Browser check</PreflightSectionLabel>
               {webSerialOk ? (
                  <PreflightOk>
                     ✓ WebSerial supported. The flasher will work in this browser.
                  </PreflightOk>
               ) : (
                  <PreflightBlocker>
                     <strong>WebSerial not available.</strong> The in-browser flasher
                     only runs on Chromium-based desktop browsers (Chrome, Edge, Brave,
                     Opera, Arc). Safari and Firefox can't run it. Open the dashboard
                     in a supported browser to continue, or skip the flasher and use
                     PlatformIO manually.
                  </PreflightBlocker>
               )}
            </PreflightSection>

            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
               <Spacer />
               <div onClick={() => webSerialOk && setPreflightDone(true)}>
                  <Btn
                     title="Continue →"
                     type={webSerialOk ? 'normal' : 'disabled'}
                     size="small"
                     bgcolor={webSerialOk ? colors.PRIMARY : colors.GRAY10}
                     textcolor={webSerialOk ? colors.WHITE : colors.GRAY1}
                  />
               </div>
            </BtnRow>
         </Wrapper>
      );
   }

   const currentStepLabel = STEPS[step - 1].label;

   return (
      <Wrapper $wide={step === 4 && !!bootstrapLine}>
         <Stepper>
            {STEPS.map((s, i) => (
               <React.Fragment key={s.num}>
                  <Bullet $active={s.num === step} $done={s.num < step}>
                     {s.num < step ? '✓' : s.num}
                  </Bullet>
                  {i < STEPS.length - 1 && <Connector $done={s.num < step} />}
               </React.Fragment>
            ))}
         </Stepper>
         <StepLabel>
            Step {step} of {STEPS.length} — <strong>{currentStepLabel}</strong>
         </StepLabel>

         <StepBody>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}
         </StepBody>

         {renderNav()}
      </Wrapper>
   );
};

/*
 * Bypass panel — appears on Step 5 (Done) so the operator can mark
 * their own laptop / setup PC as bypassed BEFORE leaving the wizard.
 * Without this, the operator's machine loses internet the moment they
 * unplug from their separate WAN (e.g. home WiFi) and rely solely on
 * the hEX's LAN — the hotspot intercepts everything.
 *
 * Fetches the router's current DHCP lease table + ip-binding list in
 * parallel. Renders two sections: "Already bypassed" (existing bindings)
 * and "Connected devices" (DHCP leases not yet bypassed). Operator
 * clicks "Bypass" on a connected device row to add it; "Remove" on a
 * bypass row to take it off.
 */
const BypassPanel: React.FC<{ deviceId: string | null }> = ({ deviceId }) => {
   const qc = useQueryClient();
   const leasesQuery = useQuery({
      queryKey: ['dhcp-leases', deviceId],
      queryFn: () => listDhcpLeases(deviceId!),
      enabled: !!deviceId,
      refetchInterval: 5000,
   });
   const bindingsQuery = useQuery({
      queryKey: ['ip-bindings', deviceId],
      queryFn: () => listIpBindings(deviceId!),
      enabled: !!deviceId,
      refetchInterval: 5000,
   });

   const bindings = bindingsQuery.data?.bindings ?? [];
   const leases = leasesQuery.data?.leases ?? [];
   const bypassedMacs = new Set(bindings.map((b) => b.mac));
   const addable = leases.filter((l) => l.mac && !bypassedMacs.has(l.mac));

   const handleAdd = async (lease: DhcpLease) => {
      if (!deviceId) return;
      try {
         const label = lease.hostname?.trim()
            ? `Operator: ${lease.hostname}`
            : `Operator device (${lease.address})`;
         const res = await addIpBinding(deviceId, lease.mac, label);
         if (res.added) {
            toast.success(`Bypassed ${lease.mac}`);
         } else {
            toast.info(`${lease.mac} was already bypassed`);
         }
         qc.invalidateQueries({ queryKey: ['ip-bindings', deviceId] });
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
               ?.error || 'Could not add bypass';
         toast.error(msg);
      }
   };

   const handleRemove = async (binding: IpBindingEntry) => {
      if (!deviceId) return;
      try {
         await removeIpBinding(deviceId, binding.id);
         toast.success(`Removed bypass for ${binding.mac}`);
         qc.invalidateQueries({ queryKey: ['ip-bindings', deviceId] });
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
               ?.error || 'Could not remove bypass';
         toast.error(msg);
      }
   };

   if (!deviceId) return null;

   return (
      <BypassBox>
         <BypassHeader>
            <BypassHeaderIcon>🔓</BypassHeaderIcon>
            <BypassHeaderText>
               <BypassTitle>Operator device bypass</BypassTitle>
               <BypassHint>
                  The hotspot now intercepts every LAN client. Add your
                  setup laptop's MAC so it keeps internet access.
               </BypassHint>
            </BypassHeaderText>
         </BypassHeader>

         <BypassSection>
            <BypassSectionTitle>
               Already bypassed
               <SectionCount>{bindings.length}</SectionCount>
            </BypassSectionTitle>
            {bindings.length === 0 ? (
               <BypassEmpty>No devices bypassed yet.</BypassEmpty>
            ) : (
               bindings.map((b) => (
                  <BypassRow key={b.id}>
                     <BypassMac>{b.mac || '—'}</BypassMac>
                     <BypassLabel>{b.comment || b.type}</BypassLabel>
                     <BypassAction onClick={() => handleRemove(b)}>
                        Remove
                     </BypassAction>
                  </BypassRow>
               ))
            )}
         </BypassSection>

         <BypassSection>
            <BypassSectionTitle>
               Connected devices
               <SectionCount>{addable.length}</SectionCount>
            </BypassSectionTitle>
            {leasesQuery.isLoading ? (
               <BypassEmpty>Reading DHCP leases…</BypassEmpty>
            ) : addable.length === 0 ? (
               <BypassEmpty>
                  🔌 Plug your laptop into a hEX LAN port — it will
                  appear here in a few seconds.
               </BypassEmpty>
            ) : (
               addable.map((l) => (
                  <BypassRow key={l.mac}>
                     <BypassMac>{l.mac}</BypassMac>
                     <BypassLabel>
                        {l.hostname || l.address}
                        {l.address && l.hostname ? ` · ${l.address}` : ''}
                     </BypassLabel>
                     <BypassActionPrimary onClick={() => handleAdd(l)}>
                        + Bypass
                     </BypassActionPrimary>
                  </BypassRow>
               ))
            )}
         </BypassSection>
      </BypassBox>
   );
};

const BypassBox = styled.div`
   margin-top: 14px;
   padding: 14px 16px;
   border-radius: 10px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
`;

const BypassHeader = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 12px;
   padding-bottom: 12px;
   border-bottom: 1px solid ${colors.GRAY12};
`;

const BypassHeaderIcon = styled.div`
   flex-shrink: 0;
   width: 32px;
   height: 32px;
   border-radius: 50%;
   background: ${colors.PRIMARY}15;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 16px;
`;

const BypassHeaderText = styled.div`
   flex: 1;
   min-width: 0;
`;

const BypassTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 3px;
`;

const BypassHint = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const BypassSection = styled.div`
   margin-top: 12px;
`;

const BypassSectionTitle = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.1em;
   color: ${colors.GRAY3};
   margin-bottom: 8px;
`;

const SectionCount = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 18px;
   height: 18px;
   padding: 0 6px;
   border-radius: 9px;
   background: ${colors.GRAY12};
   color: ${colors.GRAY3};
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0;
`;

const BypassEmpty = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY3};
   padding: 14px 12px;
   text-align: center;
   background: ${colors.GRAY14};
   border-radius: 8px;
   border: 1px dashed ${colors.GRAY12};
   line-height: 1.5;
`;

const BypassRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 8px 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   margin-bottom: 6px;
   transition: border-color 120ms ease;
   &:hover { border-color: ${colors.PRIMARY}55; }
`;

const BypassMac = styled.div`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 11.5px;
   color: ${colors.MAIN_DARK};
   font-weight: 600;
   min-width: 130px;
`;

const BypassLabel = styled.div`
   flex: 1;
   font-size: 11.5px;
   color: ${colors.GRAY3};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const BypassAction = styled.button`
   background: transparent;
   color: ${colors.GRAY3};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   padding: 5px 12px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   transition: all 120ms ease;
   &:hover {
      color: ${colors.RED};
      border-color: ${colors.RED};
      background: ${colors.RED}10;
   }
`;

const BypassActionPrimary = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: 1px solid ${colors.PRIMARY};
   border-radius: 6px;
   padding: 5px 12px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   transition: opacity 120ms ease;
   &:hover { opacity: 0.88; }
`;

const Wrapper = styled.div<{ $wide?: boolean }>`
   width: ${({ $wide }) => ($wide ? '900px' : '520px')};
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
   transition: width 0.2s ease;
   @media (max-width: 768px) { width: 100%; }
`;

/* Pre-flight gate */

const PreflightTitle = styled.h3`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin: 4px 0 6px;
`;

const BackLink = styled.button`
   margin-top: 16px;
   background: none;
   border: 0;
   color: ${colors.MAIN_DARK};
   font-size: 13px;
   cursor: pointer;
   opacity: 0.75;
   &:hover { opacity: 1; }
`;

const TierRow = styled.div`
   /* 🔴 EXPLICIT 3 columns, not auto-fit: 6 device cards stacked in one column
      made the modal taller than the screen (Ray, 2026-09-17). Two rows of three.
      Drops to two then one column only when the modal itself is narrow. */
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 12px;
   margin: 14px 0 18px;
   @media (max-width: 760px) {
      grid-template-columns: repeat(2, 1fr);
   }
   @media (max-width: 520px) {
      grid-template-columns: 1fr;
   }
`;

/* The device chooser is FOUR rows of three, which made the modal taller than a
   small laptop screen. So the chooser caps itself at the prompt shell's 85vh and
   the GRID is the part that scrolls; the "Choose your device" title and the
   Cancel row stay put, and row 1 (the new boards) is always in view. Under
   768px the shell's own body already scrolls, so no inner cap there.
   MEASURED on app-staging 2026-10-19: the tallest card is 142px; one spare line
   → 160px. Three rows = 3 × 160 + 2 × 12 gap = 504px: the grid shows EXACTLY
   three rows on every screen and scrolls for the rest. */
const CHOOSER_CARD_H = 160;
const CHOOSER_ROW_GAP = 12;
const CHOOSER_ROWS = 3;
const ChooserWrapper = styled(Wrapper)`
   max-height: 85vh;
   max-height: 85dvh;
   min-height: 0;
   @media (max-width: 768px) {
      max-height: none;
   }
`;
const ChooserGrid = styled(TierRow)`
   flex: 1 1 auto;
   min-height: 0;
   gap: ${CHOOSER_ROW_GAP}px;
   grid-auto-rows: ${CHOOSER_CARD_H}px;
   max-height: ${CHOOSER_ROWS * CHOOSER_CARD_H + (CHOOSER_ROWS - 1) * CHOOSER_ROW_GAP}px;
   overflow-y: auto;
   overscroll-behavior: contain;
   /* Keep the card border off the scrollbar. */
   padding-right: 4px;
   @media (max-width: 768px) {
      flex: none;
      grid-auto-rows: auto;
      max-height: none;
      overflow-y: visible;
      padding-right: 0;
   }
`;

// The Cloud / Hybrid step shows two cards, so two columns: in the three-column
// device row they sat squeezed beside an empty third.
const ModeRow = styled(TierRow)`
   grid-template-columns: repeat(2, 1fr);
   @media (max-width: 520px) {
      grid-template-columns: 1fr;
   }
`;

const TierCard = styled.button`
   position: relative;
   width: 100%;
   height: 100%;
   display: flex;
   flex-direction: column;
   align-items: flex-start;
   text-align: left;
   gap: 10px;
   /* Room on the right for the picture, which is pinned to that corner. */
   padding: 14px 72px 14px 16px;
   border-radius: 12px;
   border: 1.5px solid ${colors.GRAY10};
   background: ${colors.WHITE};
   cursor: pointer;
   transition: border-color 0.15s, background 0.15s;
   &:hover {
      border-color: ${colors.PRIMARY};
      background: ${colors.PRIMARY}06;
   }
   &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
      &:hover {
         border-color: ${colors.GRAY10};
         background: ${colors.WHITE};
      }
   }
`;

const TierIcon = styled.div`
   position: absolute;
   top: 12px;
   right: 14px;
   font-size: 30px;
   line-height: 1;
   flex-shrink: 0;
`;

/* An uploaded picture, sized to the emoji it replaces so a card keeps its
   height whether the type has a photo or not. */
const TierIconImg = styled.img`
   display: block;
   width: 44px;
   height: 44px;
   object-fit: contain;
   border-radius: 8px;
`;

const TierText = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

/* "New" next to a board added in this release (360 T6M, OrangePi Zero 3,
   OrangePi PC) so the operator spots it on row 1. Drop it when it stops being new. */
const NewPill = styled.span`
   display: inline-block;
   margin-left: 6px;
   padding: 1px 7px;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   vertical-align: middle;
   color: #065f46;
   background: #d1fae5;
   border: 1px solid #6ee7b7;
`;
const TierName = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const TierNameRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;


const TierModel = styled.div`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   margin-bottom: 2px;
`;

const TierDesc = styled.div`
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.GRAY3};
   /* Safety only: a fixed-height chooser card must never spill text over the
      card below. 160px leaves a spare line, so this should never bite. */
   overflow: hidden;
`;

const PreflightSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 10px 12px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;

const PreflightSectionLabel = styled.p`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const PreflightOk = styled.div`
   font-size: 12px;
   color: ${colors.SUCCESS};
   font-weight: 600;
`;

const PreflightBlocker = styled.div`
   font-size: 12px;
   color: ${colors.DANGER};
   line-height: 1.5;
   strong { color: ${colors.DANGER}; }
`;

/* Stepper */

const Stepper = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
   padding: 4px 2px;
`;

const Bullet = styled.div<{ $active: boolean; $done: boolean }>`
   width: 22px;
   height: 22px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 11px;
   font-weight: 700;
   flex-shrink: 0;
   ${({ $active, $done }) => {
      if ($done) return `background: ${colors.SUCCESS}; color: ${colors.WHITE};`;
      if ($active) return `background: ${colors.PRIMARY}; color: ${colors.WHITE}; box-shadow: 0 0 0 3px ${colors.PRIMARY}25;`;
      return `background: ${colors.GRAY12}; color: ${colors.GRAY5};`;
   }}
`;

const Connector = styled.div<{ $done: boolean }>`
   flex: 1;
   height: 2px;
   background: ${({ $done }) => ($done ? colors.SUCCESS : colors.GRAY12)};
   transition: background 0.2s;
`;

const StepLabel = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   text-align: center;
   margin-bottom: 4px;
   strong {
      color: ${colors.MAIN_DARK};
   }
`;

const StepBody = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

/* Step 4 two-column layout: single column normally; on a failed apply the
   error + diagnostic move to a right-hand column so a long error scrolls in
   its own box instead of growing the modal. Stacks on narrow screens. */
const Step4Split = styled.div<{ $split: boolean }>`
   display: ${({ $split }) => ($split ? 'grid' : 'block')};
   grid-template-columns: ${({ $split }) => ($split ? 'minmax(0, 1fr) minmax(0, 360px)' : '1fr')};
   gap: 14px;
   align-items: start;
   @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 10px;
   }
`;

const Step4Main = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   min-width: 0;
`;

const Step4Side = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   min-width: 0;
`;

/* Zero-balance choice cards */

const LicenseSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const SectionLabel = styled.p`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const ChoiceCard = styled.button<{ $selected: boolean; $static?: boolean }>`
   background: ${({ $selected }) => ($selected ? `${colors.PRIMARY}08` : colors.WHITE)};
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 8px 10px;
   cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};
   text-align: left;
   font-family: inherit;
   display: flex;
   flex-direction: column;
   gap: 2px;
   transition: all 0.15s;
   position: relative;
   &:hover { border-color: ${({ $static }) => ($static ? colors.PRIMARY : colors.PRIMARY)}; }
`;

const ChoiceIcon = styled.span`
   font-size: 18px;
`;

const ChoiceTitle = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const ChoiceDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const ChoiceTag = styled.span<{ $green?: boolean }>`
   position: absolute;
   top: 6px;
   right: 6px;
   font-size: 9px;
   font-weight: 700;
   padding: 2px 6px;
   border-radius: 10px;
   background: ${({ $green }) => ($green ? `${colors.SUCCESS}15` : colors.GRAY14)};
   color: ${({ $green }) => ($green ? colors.SUCCESS : colors.GRAY3)};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const AutoRenewRow = styled.div`
   display: flex;
   gap: 10px;
   padding: 10px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const AutoRenewCheckbox = styled.input`
   width: 16px;
   height: 16px;
   margin-top: 2px;
   accent-color: ${colors.PRIMARY};
   cursor: pointer;
`;

const AutoRenewLabel = styled.label`
   display: flex;
   flex-direction: column;
   gap: 2px;
   cursor: pointer;
   flex: 1;
`;

const AutoRenewTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const AutoRenewDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const BuyCreditsCard = styled.button`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px;
   border: 1px dashed ${colors.PRIMARY};
   background: ${colors.PRIMARY}08;
   border-radius: 10px;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   transition: background 0.15s;
   &:hover { background: ${colors.PRIMARY}12; }
`;

const BuyCreditsIcon = styled.span`
   font-size: 18px;
`;

const BuyCreditsText = styled.span`
   display: flex;
   flex-direction: column;
   gap: 2px;
   flex: 1;
`;

const BuyCreditsTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const BuyCreditsDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const BuyCreditsArrow = styled.span`
   font-size: 18px;
   color: ${colors.PRIMARY};
   font-weight: 700;
`;

/* Hardware step */

const HardwareSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const KitNote = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   padding: 8px 10px;
   background: ${colors.GRAY14};
   border-radius: 6px;
`;

const FieldLabel = styled.span`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   margin-top: 4px;
`;

const SsidHint = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   margin-top: -4px;
`;

// Two fields side-by-side (e.g. SSID + password) to keep the modal compact.
const FieldRow = styled.div`
   display: flex;
   gap: 12px;
   & > * {
      flex: 1;
      min-width: 0;
   }
`;

const Select = styled.select`
   font-family: inherit;
   font-size: 13px;
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
`;

const RadioRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 8px;
   @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const RadioCard = styled.button<{ $selected: boolean }>`
   background: ${({ $selected }) => ($selected ? `${colors.PRIMARY}08` : colors.WHITE)};
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 8px;
   padding: 8px 10px;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   display: flex;
   flex-direction: column;
   gap: 2px;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const RadioTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const RadioDesc = styled.span`
   font-size: 10px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

/* Advanced */

const AdvancedNote = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

/* Step-2 port-role banner */

const ROLE_META: Record<PortRole, { label: string; color: string }> = {
   wan: { label: 'WAN', color: colors.GRAY },
   customer: { label: 'Customer', color: colors.GREEN },
   trunk: { label: 'Trunk', color: colors.PRIMARY },
   owner: { label: 'Owner', color: colors.ORANGE },
};

const PortBanner = styled.div`
   margin-top: 4px;
   padding: 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const PortBannerTitle = styled.span`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
`;

const PortTabs = styled.div`
   display: inline-flex;
   gap: 4px;
   padding: 3px;
   background: #e5e7eb;
   border-radius: 9px;
   align-self: flex-start;
   margin-bottom: 8px;
`;
const PortTab = styled.button<{ $on: boolean }>`
   border: 0;
   border-radius: 7px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   background: ${({ $on }) => ($on ? '#fff' : 'transparent')};
   color: ${({ $on }) => ($on ? '#111827' : '#6b7280')};
   box-shadow: ${({ $on }) => ($on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none')};
`;
const PortRowWrap = styled.div`
   display: grid;
   /* Equal-width columns that fill the row and wrap cleanly for big routers
      (CCR can have 15+ ports). auto-fit collapses empty tracks so the chips
      always stretch edge-to-edge with no trailing gap. */
   grid-template-columns: repeat(auto-fit, minmax(78px, 1fr));
   gap: 6px;
   align-items: stretch;
`;

const PortChip = styled.div<{ $color: string }>`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 2px;
   padding: 10px 8px;
   border-radius: 6px;
   border: 1px solid ${({ $color }) => $color};
   background: ${({ $color }) => `${$color}14`};
`;

const PortChipPort = styled.span`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const PortChipRole = styled.span<{ $color: string }>`
   font-size: 9px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.03em;
   color: ${({ $color }) => $color};
`;

const PortLegend = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   margin: 0;
`;

/* Manual placeholders for steps 3-5 */

const ComingSoonTag = styled.span`
   align-self: flex-start;
   font-size: 9px;
   font-weight: 700;
   padding: 3px 8px;
   border-radius: 10px;
   background: ${colors.PRIMARY}15;
   color: ${colors.PRIMARY};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const DEVICE_MODE_PREP_CMD = '/system device-mode update mode=advanced';

const ManualBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 10px 12px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;


const StepCard = styled.div<{ $active: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 12px 14px;
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}0d` : colors.GRAY14)};
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease, transform 150ms ease;
   ${({ $active }) =>
      $active
         ? `box-shadow: 0 4px 16px ${colors.PRIMARY}22;`
         : `&:hover {
               border-color: ${colors.PRIMARY}66;
               background: ${colors.WHITE};
               transform: translateY(-1px);
               box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            }`}
`;

const ToggleHeader = styled.button`
   display: flex;
   align-items: center;
   gap: 14px;
   width: 100%;
   padding: 4px 0;
   background: transparent;
   border: none;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
`;

const StepBadge = styled.div<{ $active: boolean }>`
   flex-shrink: 0;
   width: 34px;
   height: 34px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 15px;
   font-weight: 800;
   background: ${({ $active }) => ($active ? colors.PRIMARY : colors.WHITE)};
   color: ${({ $active }) => ($active ? colors.WHITE : colors.GRAY3)};
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   transition: all 150ms ease;
`;

const StepTitleBlock = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const StepEyebrow = styled.span<{ $active: boolean }>`
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.1em;
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
`;

const StepTitle = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   line-height: 1.3;
`;

const TapHint = styled.span`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   margin-top: 4px;
`;

const Chevron = styled.span<{ $open: boolean }>`
   flex-shrink: 0;
   font-size: 20px;
   font-weight: 800;
   color: ${({ $open }) => ($open ? colors.PRIMARY : colors.GRAY3)};
   transition: transform 200ms ease;
   transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
   display: inline-block;
`;

const WhyDetails = styled.details`
   margin-top: 2px;
   font-size: 11.5px;
   color: ${colors.GRAY3};
   summary {
      cursor: pointer;
      font-weight: 600;
      color: ${colors.PRIMARY};
      list-style: none;
      &::-webkit-details-marker { display: none; }
      &::before {
         content: '▸ ';
         display: inline-block;
         transition: transform 120ms ease;
      }
   }
   &[open] summary::before {
      content: '▾ ';
   }
   summary:hover { text-decoration: underline; }
   p {
      margin-top: 8px;
      line-height: 1.6;
   }
`;

const ManualHeader = styled.p`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Ol = styled.ol<{ $start?: number }>`
   margin: 0;
   padding-left: 18px;
   ${({ $start }) => ($start ? `counter-reset: list-item ${$start - 1};` : '')}
   li {
      font-size: 12px;
      color: ${colors.GRAY3};
      line-height: 1.6;
   }
`;

const Mono = styled.code`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 11px;
   padding: 1px 5px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 4px;
   color: ${colors.MAIN_DARK};
`;

const CredsBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 10px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;

const CredsRow = styled.div`
   display: grid;
   grid-template-columns: 100px 1fr auto;
   align-items: center;
   gap: 10px;
`;

const CredsLabel = styled.span`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const CredsVal = styled.code`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 11px;
   color: ${colors.MAIN_DARK};
   word-break: break-all;
`;

const CopyBtn = styled.button`
   font-family: inherit;
   font-size: 10px;
   font-weight: 700;
   padding: 4px 10px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   &:hover { opacity: 0.9; }
`;

const ScriptRow = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
`;

const ScriptLabel = styled.div`
   flex: 1;
   font-size: 12px;
   color: ${colors.GRAY1};
   line-height: 1.4;
`;

const VerifyPanel = styled.div<{ $state: 'idle' | 'verifying' | 'verified' | 'failed' }>`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 12px 14px;
   border-radius: 8px;
   background: ${({ $state }) =>
      $state === 'verified'
         ? '#0e3a23'
         : $state === 'failed'
         ? '#3a0e0e'
         : colors.GRAY14};
   border: 1px solid ${({ $state }) =>
      $state === 'verified'
         ? '#1f7a4a'
         : $state === 'failed'
         ? '#7a1f1f'
         : colors.GRAY12};
   /* Light text on the dark verified/failed backgrounds; default dark on the
      light idle/verifying background. Header + hint inherit this. */
   color: ${({ $state }) =>
      $state === 'verified' || $state === 'failed' ? '#e5e7eb' : colors.MAIN_DARK};
`;

const VerifyHeader = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   font-size: 13px;
   color: inherit;
`;

const VerifyHint = styled.span`
   font-size: 11px;
   color: inherit;
   opacity: 0.8;
   font-weight: 400;
`;

const VerifyChecklist = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const VerifyCheckRow = styled.div<{ $ok: boolean }>`
   font-size: 12px;
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   color: ${({ $ok }) => ($ok ? '#34d399' : '#f87171')};
`;

const VerifyErr = styled.pre`
   font-size: 11px;
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   white-space: pre-wrap;
   word-break: break-word;
   color: #fca5a5;
   background: #1f1313;
   padding: 8px 10px;
   border-radius: 6px;
   margin: 0;
   /* Cap height + scroll so a long RouterOS error can't grow past the screen. */
   max-height: 260px;
   overflow: auto;
`;

const DevModeBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 8px 10px;
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 6px;
   margin-bottom: 8px;
`;

const DevModeRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 12px;
   color: ${colors.GRAY3};
   label { cursor: pointer; }
`;

const DevModeCheck = styled.input`
   width: 16px;
   height: 16px;
   cursor: pointer;
`;

const DevModeIpRow = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const DevModeIpLabel = styled.label`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;

const DevModeIpInput = styled.input`
   padding: 6px 8px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const OperatorMacBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 10px 12px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   margin-bottom: 8px;
`;

const OperatorMacHint = styled.div`
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.GRAY1};
`;

const OperatorMacInput = styled.input`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: monospace;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   text-transform: uppercase;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const OperatorMacErr = styled.div`
   font-size: 11px;
   color: ${colors.DANGER || '#dc3545'};
`;

const ScriptPre = styled.pre`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 10px;
   line-height: 1.5;
   padding: 10px;
   background: ${colors.MAIN_DARK};
   color: ${colors.WHITE};
   border-radius: 8px;
   max-height: 160px;
   overflow: auto;
   white-space: pre-wrap;
   word-break: break-all;
`;

/* Step 4 live detection */

const pulse = keyframes`
   0%   { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
   70%  { box-shadow: 0 0 0 8px transparent; opacity: 0.7; }
   100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
`;

const DetectPanel = styled.div<{ $detected: boolean }>`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 10px 12px;
   border-radius: 8px;
   background: ${({ $detected }) => ($detected ? `${colors.SUCCESS}10` : colors.GRAY14)};
   border: 1px solid ${({ $detected }) => ($detected ? `${colors.SUCCESS}40` : colors.GRAY12)};
`;

const DetectDot = styled.span<{ $detected: boolean }>`
   width: 10px;
   height: 10px;
   border-radius: 50%;
   flex-shrink: 0;
   color: ${({ $detected }) => ($detected ? colors.SUCCESS : colors.PRIMARY)};
   background: currentColor;
   animation: ${pulse} 1.4s infinite;
`;

const DetectText = styled.div`
   flex: 1;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   line-height: 1.4;
   strong {
      font-weight: 700;
   }
`;

const DetectSub = styled.span`
   display: block;
   font-size: 10px;
   color: ${colors.GRAY5};
   margin-top: 2px;
   font-weight: 400;
`;

const RetryRow = styled.div`
   display: flex;
   gap: 6px;
   align-items: center;
   flex-shrink: 0;
`;

const SkipLink = styled.button`
   font-family: inherit;
   font-size: 10px;
   font-weight: 700;
   padding: 4px 10px;
   background: transparent;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   white-space: nowrap;
   &:hover {
      color: ${colors.MAIN_DARK};
      border-color: ${colors.GRAY10};
   }
`;

/* Step 5 success */

const SuccessBanner = styled.div`
   display: flex;
   gap: 12px;
   align-items: center;
   padding: 12px 14px;
   background: ${colors.SUCCESS}10;
   border: 1px solid ${colors.SUCCESS}30;
   border-radius: 10px;
`;

const SuccessIcon = styled.span`
   font-size: 24px;
   font-weight: 700;
   color: ${colors.SUCCESS};
   flex-shrink: 0;
`;

const SuccessText = styled.div`
   flex: 1;
`;

const SuccessTitle = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 4px;
`;

const COINBOX_SILENT_AFTER_MS = 3 * 60 * 1000;

const CoinBoxSilent = styled.div`
   margin: 0 0 14px;
   padding: 12px 14px;
   border-radius: 10px;
   background: #fff4e5;
   border: 1px solid #f5b971;
   color: #7a4500;
   font-size: 14px;
   line-height: 1.5;
`;

const SuccessSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const BtnRow = styled.div`
   display: flex;
   gap: 8px;
   margin-top: 4px;
`;

const Spacer = styled.div`
   flex: 1;
`;

const WgPanel = styled.div<{ $up: boolean }>`
   border: 1px solid ${({ $up }) => ($up ? '#22c55e' : '#f59e0b')};
   background: ${({ $up }) => ($up ? '#f0fdf4' : '#fffbeb')};
   color: ${({ $up }) => ($up ? '#166534' : '#92400e')};
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 13px;
   line-height: 1.5;
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const WgPanelHint = styled.span`
   font-size: 11px;
   opacity: 0.85;
`;

export default DeviceRegister;

const BetaBadge = styled.span`
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: #9a3412;
   background: #ffedd5;
   border: 1px solid #fdba74;
   border-radius: 999px;
   padding: 1px 7px;
`;

const ClosedBadge = styled.span`
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: #4b5563;
   background: #f3f4f6;
   border: 1px solid #d1d5db;
   border-radius: 999px;
   padding: 1px 7px;
`;
